import { Navigate, type RouteObject } from 'react-router-dom';

import { AdminOnlyRoute } from '@/components/auth/AdminOnlyRoute';
import { useAuth } from '@/hooks/useAuth';
import { lazyPage } from '@/lib/lazy-page';

const OwnerOverviewPage = lazyPage(() => import('@/pages/stock/owner-overview/OwnerOverviewPage'));

const StockLayout = lazyPage(() => import('@/pages/stock/StockLayout'));

export const SELLER_WORKSPACE_PATH = '/webadmin/app/stock/seller-workspace';

// Stock module pages
const StockProductsPage = lazyPage(() => import('@/pages/stock/ProductsPage'));
const StockProductCategoriesPage = lazyPage(() => import('@/pages/stock/ProductCategoriesPage'));
const StockProductWorkspacePage = lazyPage(() => import('@/pages/stock/ProductWorkspacePage'));
const StockMaterialsPage = lazyPage(() => import('@/pages/stock/MaterialsPage'));
const StockRecipesPage = lazyPage(() => import('@/pages/stock/RecipesPage'));
const StockRecipeCreatePage = lazyPage(() => import('@/pages/stock/RecipeCreatePage'));
const StockIngredientViewPage = lazyPage(() => import('@/pages/stock/IngredientViewPage'));
const StockRecipeViewPage = lazyPage(() => import('@/pages/stock/RecipeViewPage'));
const StockRecipeEditPage = lazyPage(() => import('@/pages/stock/RecipeEditPage'));
const StockBalancesPage = lazyPage(() => import('@/pages/stock/BalancesPage'));
const StockBalancesDetailPage = lazyPage(() => import('@/pages/stock/BalancesDetailPage'));
const StockReceiptsPage = lazyPage(() => import('@/pages/stock/ReceiptsPage'));
const StockReceiptCreatePage = lazyPage(() => import('@/pages/stock/ReceiptCreatePage'));
const StockReceiptViewPage = lazyPage(() => import('@/pages/stock/ReceiptViewPage'));
const StockReceiptEditPage = lazyPage(() => import('@/pages/stock/ReceiptEditPage'));
const StockTransfersPage = lazyPage(() => import('@/pages/stock/TransfersPage'));
const StockTransferCreatePage = lazyPage(() => import('@/pages/stock/TransferCreatePage'));
const StockTransferViewPage = lazyPage(() => import('@/pages/stock/TransferViewPage'));
const StockTransferEditPage = lazyPage(() => import('@/pages/stock/TransferEditPage'));
const StockSellerRequestsPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/RequestsPage'),
);
const StockSupplierReceivingPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/SupplierReceivingPage'),
);
const StockSellerBalancesPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/BalancesPage'),
);
const StockSellerAdminPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/AdminOverviewPage'),
);
const StockSellerApprovalPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/AdminApprovalPage'),
);
const StockSellerAccessPage = lazyPage(() => import('@/pages/stock/seller-workspace/AccessPage'));
const StockSellerSaleChangesPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/SaleChangesPage'),
);

const StockSellerSaleChangesReviewPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/SaleChangesReviewPage'),
);

const StockSellerWriteOffRequestsPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/WriteOffRequestsPage'),
);
const StockSellerProductionRequestsPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/ProductionRequestsPage'),
);
const StockSellerExpiryPage = lazyPage(() => import('@/pages/stock/seller-workspace/ExpiryPage'));
const StockSellerShelfCountsPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/ShelfCountsPage'),
);
const StockSellerExpiryRiskPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/ExpiryRiskPage'),
);
const StockSellerScannerPage = lazyPage(
  () => import('@/pages/stock/seller-workspace/ProductScannerPage'),
);
const StockSellerBarcodeQueuePage = lazyPage(
  () => import('@/pages/stock/seller-workspace/BarcodeQueuePage'),
);
const StockWriteOffsPage = lazyPage(() => import('@/pages/stock/WriteOffsPage'));
const StockWriteOffCreatePage = lazyPage(() => import('@/pages/stock/WriteOffCreatePage'));
const StockWriteOffViewPage = lazyPage(() => import('@/pages/stock/WriteOffViewPage'));
const StockWriteOffEditPage = lazyPage(() => import('@/pages/stock/WriteOffEditPage'));
const StockB2BSalesPage = lazyPage(() => import('@/pages/stock/B2BSalesPage'));
const StockSalesInvoicesPage = lazyPage(() => import('@/pages/stock/SalesInvoicesPage'));
const StockPlatformSalesPage = lazyPage(() => import('@/pages/stock/PlatformSalesPage'));
const StockB2BSaleCreatePage = lazyPage(() => import('@/pages/stock/B2BSaleCreatePage'));
const StockB2BSaleViewPage = lazyPage(() => import('@/pages/stock/B2BSaleViewPage'));
const StockB2BSaleEditPage = lazyPage(() => import('@/pages/stock/B2BSaleEditPage'));
const StockB2BSaleShipmentCreatePage = lazyPage(
  () => import('@/pages/stock/B2BSaleShipmentCreatePage'),
);
const StockB2BSaleShipmentViewPage = lazyPage(
  () => import('@/pages/stock/B2BSaleShipmentViewPage'),
);
const StockB2BSaleShipmentEditPage = lazyPage(
  () => import('@/pages/stock/B2BSaleShipmentEditPage'),
);
const StockProductionsPage = lazyPage(() => import('@/pages/stock/ProductionsPage'));
const StockHotReportsPage = lazyPage(() => import('@/pages/stock/HotReportsPage'));
const StockProductionCreatePage = lazyPage(() => import('@/pages/stock/ProductionCreatePage'));
const StockProductionApprovalPage = lazyPage(
  () => import('./seller-workspace/ProductionApprovalPage'),
);
const StockProductionViewPage = lazyPage(() => import('@/pages/stock/ProductionViewPage'));
const StockProductionEditPage = lazyPage(() => import('@/pages/stock/ProductionEditPage'));
const StockInventoriesPage = lazyPage(() => import('@/pages/stock/InventoriesPage'));
const StockInventoryCreatePage = lazyPage(() => import('@/pages/stock/InventoryCreatePage'));
const StockInventoryDetailPage = lazyPage(() => import('@/pages/stock/InventoryDetailPage'));
const StockSupplierReturnsPage = lazyPage(() => import('@/pages/stock/SupplierReturnsPage'));
const StockSupplierReturnCreatePage = lazyPage(
  () => import('@/pages/stock/SupplierReturnCreatePage'),
);
const StockSupplierReturnViewPage = lazyPage(() => import('@/pages/stock/SupplierReturnViewPage'));
const StockSupplierReturnEditPage = lazyPage(() => import('@/pages/stock/SupplierReturnEditPage'));

const STOCK_CONTENT_SCROLL_EXACT_ROUTES = [
  '/webadmin/app/stock/operations',
  '/webadmin/app/stock/products',
  '/webadmin/app/stock/products/categories',
  '/webadmin/app/stock/balances',
  '/webadmin/app/stock/receipts',
  '/webadmin/app/stock/transfers',
  '/webadmin/app/stock/write-offs',
  '/webadmin/app/stock/b2b-sales',
  '/webadmin/app/stock/sales-invoices',
  '/webadmin/app/stock/platform-sales',
  '/webadmin/app/stock/productions',
  '/webadmin/app/stock/hot-reports',
  '/webadmin/app/stock/inventories',
  '/webadmin/app/stock/supplier-returns',
  '/webadmin/app/stock/seller-workspace/overview',
  '/webadmin/app/stock/seller-workspace',
  '/webadmin/app/stock/seller-workspace/requests',
  '/webadmin/app/stock/seller-workspace/balances',
  '/webadmin/app/stock/seller-workspace/scanner',
  '/webadmin/app/stock/seller-workspace/expiry',
  '/webadmin/app/stock/seller-workspace/counts',
  '/webadmin/app/stock/seller-workspace/expiry-risk',
  '/webadmin/app/stock/seller-workspace/new-barcodes',
] as const;

const STOCK_CONTENT_SCROLL_ROUTE_PREFIXES = [
  '/webadmin/app/stock/seller-workspace/admin/productions/',
  '/webadmin/app/stock/ingredients',
  '/webadmin/app/stock/recipes',
  '/webadmin/app/stock/transfers/',
  '/webadmin/app/stock/receipts/',
  '/webadmin/app/stock/productions/',
  '/webadmin/app/stock/write-offs/',
  '/webadmin/app/stock/b2b-sales/',
  '/webadmin/app/stock/supplier-returns/',
  '/webadmin/app/stock/inventories/',
] as const;

export function isStockContentScrollRoute(pathname: string): boolean {
  return (
    STOCK_CONTENT_SCROLL_EXACT_ROUTES.includes(
      pathname as (typeof STOCK_CONTENT_SCROLL_EXACT_ROUTES)[number],
    ) || STOCK_CONTENT_SCROLL_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

function StockSellerWorkspaceEntryPage() {
  const { user } = useAuth({ skipInitialCheck: true });
  if (user?.role === 'admin') {
    return <Navigate to={`${SELLER_WORKSPACE_PATH}/admin`} replace />;
  }
  return <StockSellerRequestsPage />;
}

export const stockRoute: RouteObject = {
  element: <StockLayout />,
  children: [
    {
      path: 'stock/products',
      element: <StockProductsPage />,
    },
    {
      path: 'stock/products/categories',
      element: <StockProductCategoriesPage />,
    },
    {
      path: 'stock/products/:productId',
      element: <StockProductWorkspacePage />,
    },
    {
      path: 'stock/ingredients',
      element: <StockMaterialsPage />,
    },
    {
      path: 'stock/ingredients/:id',
      element: <StockIngredientViewPage />,
    },
    {
      path: 'stock/supplies',
      element: <Navigate to="/webadmin/app/stock/ingredients" replace />,
    },
    {
      path: 'stock/recipes',
      element: <StockRecipesPage />,
    },
    {
      path: 'stock/recipes/new',
      element: <StockRecipeCreatePage />,
    },
    {
      path: 'stock/recipes/:id',
      element: <StockRecipeViewPage />,
    },
    {
      path: 'stock/recipes/:id/edit',
      element: <StockRecipeEditPage />,
    },
    {
      path: 'stock/balances',
      element: <StockBalancesPage />,
    },
    {
      path: 'stock/balances/:productId',
      element: <StockBalancesDetailPage />,
    },
    {
      path: 'stock/receipts',
      element: <StockReceiptsPage />,
    },
    {
      path: 'stock/receipts/new',
      element: <StockReceiptCreatePage />,
    },
    {
      path: 'stock/receipts/:id/edit',
      element: <StockReceiptEditPage />,
    },
    {
      path: 'stock/receipts/:id',
      element: <StockReceiptViewPage />,
    },
    {
      path: 'stock/transfers/new',
      element: <StockTransferCreatePage />,
    },
    {
      path: 'stock/transfers/:id',
      element: <StockTransferViewPage />,
    },
    {
      path: 'stock/transfers/:id/edit',
      element: <StockTransferEditPage />,
    },
    {
      path: 'stock/transfers',
      element: <StockTransfersPage />,
    },
    {
      path: 'stock/seller-workspace',
      element: <StockSellerWorkspaceEntryPage />,
    },
    {
      path: 'stock/seller-workspace/requests',
      element: <StockSellerRequestsPage />,
    },
    {
      path: 'stock/seller-workspace/receiving',
      element: <StockSupplierReceivingPage />,
    },
    {
      path: 'stock/seller-workspace/receiving/:sessionId',
      element: <StockSupplierReceivingPage />,
    },
    {
      path: 'stock/seller-workspace/balances',
      element: <StockSellerBalancesPage />,
    },
    {
      path: 'stock/seller-workspace/overview',
      element: <OwnerOverviewPage />,
    },
    {
      path: 'stock/seller-workspace/overview/inventory',
      element: <OwnerOverviewPage inventory />,
    },
    {
      path: 'stock/seller-workspace/admin',
      element: <StockSellerAdminPage />,
    },
    {
      path: 'stock/seller-workspace/admin/productions/:id',
      element: <StockProductionApprovalPage />,
    },
    {
      path: 'stock/seller-workspace/admin/:id',
      element: <StockSellerApprovalPage />,
    },
    {
      path: 'stock/seller-workspace/sale-reviews',
      element: <StockSellerSaleChangesReviewPage />,
    },
    {
      path: 'stock/seller-workspace/sale-changes',
      element: <StockSellerSaleChangesPage />,
    },
    {
      path: 'stock/seller-workspace/write-offs',
      element: <StockSellerWriteOffRequestsPage />,
    },
    {
      path: 'stock/seller-workspace/productions',
      element: <StockSellerProductionRequestsPage />,
    },
    {
      path: 'stock/seller-workspace/scanner',
      element: <StockSellerScannerPage />,
    },
    {
      path: 'stock/seller-workspace/expiry',
      element: <StockSellerExpiryPage />,
    },
    {
      path: 'stock/seller-workspace/counts',
      element: <StockSellerShelfCountsPage />,
    },
    {
      path: 'stock/seller-workspace/expiry-risk',
      element: <StockSellerExpiryRiskPage />,
    },
    {
      path: 'stock/seller-workspace/new-barcodes',
      element: <StockSellerBarcodeQueuePage />,
    },
    {
      path: 'stock/seller-workspace/access',
      element: <StockSellerAccessPage />,
    },
    {
      path: 'stock/write-offs',
      element: <StockWriteOffsPage />,
    },
    {
      path: 'stock/write-offs/new',
      element: <StockWriteOffCreatePage />,
    },
    {
      path: 'stock/write-offs/:id',
      element: <StockWriteOffViewPage />,
    },
    {
      path: 'stock/write-offs/:id/edit',
      element: <StockWriteOffEditPage />,
    },
    {
      path: 'stock/b2b-sales',
      element: <StockB2BSalesPage />,
    },
    {
      element: <AdminOnlyRoute />,
      children: [{ path: 'stock/sales-invoices', element: <StockSalesInvoicesPage /> }],
    },
    {
      path: 'stock/platform-sales',
      element: <StockPlatformSalesPage />,
    },
    {
      element: <AdminOnlyRoute />,
      children: [{ path: 'stock/hot-reports', element: <StockHotReportsPage /> }],
    },
    {
      path: 'stock/b2b-sales/new',
      element: <StockB2BSaleCreatePage />,
    },
    {
      path: 'stock/b2b-sales/:id',
      element: <StockB2BSaleViewPage />,
    },
    {
      path: 'stock/b2b-sales/:id/edit',
      element: <StockB2BSaleEditPage />,
    },
    {
      path: 'stock/b2b-sales/:sale_id/shipments/new',
      element: <StockB2BSaleShipmentCreatePage />,
    },
    {
      path: 'stock/b2b-sales/:sale_id/shipments/:shipment_id',
      element: <StockB2BSaleShipmentViewPage />,
    },
    {
      path: 'stock/b2b-sales/:sale_id/shipments/:shipment_id/edit',
      element: <StockB2BSaleShipmentEditPage />,
    },
    {
      path: 'stock/productions/new',
      element: <StockProductionCreatePage />,
    },
    {
      path: 'stock/productions/:id/edit',
      element: <StockProductionEditPage />,
    },
    {
      path: 'stock/productions/:id',
      element: <StockProductionViewPage />,
    },
    {
      path: 'stock/productions',
      element: <StockProductionsPage />,
    },
    {
      path: 'stock/inventories',
      element: <StockInventoriesPage />,
    },
    {
      path: 'stock/inventories/new',
      element: <StockInventoryCreatePage />,
    },
    {
      path: 'stock/inventories/:id/edit',
      element: <StockInventoryDetailPage />,
    },
    {
      path: 'stock/inventories/:id',
      element: <StockInventoryDetailPage />,
    },
    {
      path: 'stock/supplier-returns',
      element: <StockSupplierReturnsPage />,
    },
    {
      path: 'stock/supplier-returns/new',
      element: <StockSupplierReturnCreatePage />,
    },
    {
      path: 'stock/supplier-returns/:id',
      element: <StockSupplierReturnViewPage />,
    },
    {
      path: 'stock/supplier-returns/:id/edit',
      element: <StockSupplierReturnEditPage />,
    },
  ],
};
